import { Component } from 'react';
import propTypes from 'prop-types';
import HttpUtils from '~/agent/http-utils';
import Modal from '../modal';
import { apiGet } from '../../../common';
import isFeatureEnabled from '../is-feature-enabled';
import AssetPicker from './asset-picker';
import AssetPickerTypes from './asset-picker-types';

const CONTENT_MAPPING = [
  ['challenges', AssetPickerTypes.CHALLENGE],
  ['courses', AssetPickerTypes.GUIDE],
  ['resources', AssetPickerTypes.RESOURCE],
  ['topLevelContents', AssetPickerTypes.SCORM],
  ['trainingClasses', AssetPickerTypes.TRAINING_CLASS],
  ['trainingSeries', AssetPickerTypes.SERIES],
  ['trainingWorkflows', AssetPickerTypes.WORKFLOW],
  ['universities', AssetPickerTypes.UNIVERSITY],
  ['academyEvents', AssetPickerTypes.ACADEMY_EVENT],
  ['academyPaths', AssetPickerTypes.ACADEMY_PATH],
  ['academyCollections', AssetPickerTypes.ACADEMY_COLLECTION],
  ['academyCollectionContents', AssetPickerTypes.ACADEMY_COLLECTION_CONTENT],
  ['academyCertifications', AssetPickerTypes.ACADEMY_CERTIFICATION],
];

class AssetPickerModal extends Component {
  state = {
    folders: null,
  };

  componentDidUpdate() {
    if (this.props.show && !this.state.folders) {
      this.loadFolders();
    }
  }

  render() {
    const modalBody = this.state.folders ? (
      <AssetPicker
        showSearchBar={this.props.showSearchBar}
        searchBarForAcademy={this.props.academyId !== undefined}
        rootFolders={this.state.folders}
        updateCallback={this.loadFolders}
        viewableAssets={this.props.viewableAssets}
        currentAsset={this.props.currentAsset}
        selectableAssets={this.props.selectableAssets}
        searchableAssets={this.props.searchableAssets}
        includeUnpublishedTrainings={this.props.includeUnpublishedTrainings}
        includeUnassignableChallenges={this.props.includeUnassignableChallenges}
        onlyVisible={this.props.onlyVisible}
        excludedAssetIds={this.props.excludedAssetIds}
      >
        {this.props.children}
      </AssetPicker>
    ) : (
      <div className="text-center">
        <img className="pas" height="20" width="20" src="/images/loader.gif" />
      </div>
    );
    return (
      <Modal
        overflowVisible
        show={this.props.show}
        title={this.props.title}
        saveButtonTitle={this.props.saveButtonTitle}
        onSave={this.props.onSave}
        onClose={this.props.onClose}
        saveDisabled={this.props.saveDisabled}
        dontShowSaveButton={this.props.dontShowSaveButton}
        newUi={this.props.newUi}
      >
        <div className="mtl">{this.props.show && modalBody}</div>
        <div style={{ paddingTop: '10px', paddingLeft: '20px' }} className="input-error-msg">
          {this.props.errorMessage}
        </div>
      </Modal>
    );
  }

  loadFolders = () => {
    const {
      academyId,
      includeUnpublishedTrainings,
      includeUnassignableChallenges,
      includeEnterpriseContent = true,
      onlyVisible,
      showDraftVersionData,
      viewableAssets,
    } = this.props;
    const me = this;
    const params = {};
    if (viewableAssets.includes(AssetPickerTypes.GUIDE_TASK)) {
      params.sections_and_tasks = true;
    } else if (viewableAssets.includes(AssetPickerTypes.GUIDE_SECTION)) {
      params.sections = true;
    }

    if (includeUnpublishedTrainings) {
      params.include_unpublished_trainings = true;
    }

    if (onlyVisible && isFeatureEnabled('fy25_q3_hide_content_from_learners')) {
      params.only_visible = true;
    }

    if (includeUnassignableChallenges) {
      params.include_unassignable_challenges = true;
    }

    if (showDraftVersionData) {
      params.show_draft_version_data = true;
    }

    if (academyId) {
      params.include_academy_content_for = academyId;
      params.academy_content_types = viewableAssets;
    }

    params.include_enterprise_content = includeEnterpriseContent;

    const url = HttpUtils.buildUriWithQueryString('item_folders', params);

    apiGet(url, null, (result) => {
      me.setState({
        folders: this.filterViewableFolders(result),
      });
    });
  };

  filterViewableFolders(folders) {
    if (this.props.hideEmptyFolders) {
      const folderItemsCount = this.countViewableContent(folders);
      const viewableFolders = folders
        .filter((folder) => folderItemsCount[folder.id].total > 0)
        .map((folder) => ({ ...folder, subfolders: this.filterViewableFolders(folder.subfolders) }));

      return viewableFolders;
    }

    return folders;
  }

  /* eslint-disable no-param-reassign */
  countViewableContent(rootFolders, folderItemsCount = {}) {
    rootFolders.forEach((folder) => {
      const currentFolderCount = CONTENT_MAPPING.filter((key) => this.props.viewableAssets.includes(key[1])).reduce(
        (acc, [key]) => acc + (folder[key] ?? []).length,
        0,
      );

      folderItemsCount[folder.id] = { total: currentFolderCount };

      const subFoldersCount = folder.subfolders.length > 0 ? this.countViewableContent(folder.subfolders) : {};
      folderItemsCount[folder.id].subfolders = subFoldersCount;
      folderItemsCount[folder.id].total += Object.values(subFoldersCount).reduce((acc, sub) => acc + sub.total, 0);
    });
    return folderItemsCount;
  }
  /* eslint-enable no-param-reassign */
}

AssetPickerModal.propTypes = {
  show: propTypes.bool,
  showSearchBar: propTypes.bool,
  dontShowSaveButton: propTypes.bool,
  onSave: propTypes.func,
  onClose: propTypes.func.isRequired,
  viewableAssets: propTypes.arrayOf(propTypes.string),
  title: propTypes.string,
  errorMessage: propTypes.string,
  saveButtonTitle: propTypes.string,
  currentAsset: propTypes.oneOfType([propTypes.object, propTypes.array]),
  selectableAssets: propTypes.objectOf(propTypes.func),
  searchableAssets: propTypes.arrayOf(propTypes.string),
  children: propTypes.node,
  includeUnpublishedTrainings: propTypes.bool,
  includeUnassignableChallenges: propTypes.bool,
  onlyVisible: propTypes.bool,
  showDraftVersionData: propTypes.bool,
  hideEmptyFolders: propTypes.bool,
  excludedAssetIds: propTypes.arrayOf(propTypes.string),
};

AssetPickerModal.defaultProps = {
  showSearchBar: true,
};

export default AssetPickerModal;
